import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

const phoneNumberRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
const externalDomainRegex = /.*(?<!@ahfc\.us)$/;
export const noSpecialCharactersRegex = /^[a-zA-Z0-9\s]*$/;

export function nonZeroValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value !== 0 ? null : { nonZero: true };
  };
}

export function isValidJsonValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    try {
      JSON.parse(control.value);
      return null;
      // TS requires `any` for bare try/catch blocks
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return { invalidJson: error.message };
    }
  };
}

export function isValidExternalEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return externalDomainRegex.test(control.value) ? null : { ['Invalid external email address: cannot be in the ahfc.us domain.']: true };
  };
}

export interface stringFieldCfg {
  disabled?: boolean;
  validators?: ValidatorFn[];
  value?: string;
}

export interface nullableStringFieldCfg {
  disabled?: boolean;
  validators?: ValidatorFn[];
  value?: string | null;
}

export interface numericFieldCfg {
  disabled?: boolean;
  validators?: ValidatorFn[];
  value?: number;
}

export interface nullableNumericFieldCfg {
  disabled?: boolean;
  validators?: ValidatorFn[];
  value?: number | null;
}

export interface fileFieldCfg {
  disabled?: boolean;
  validators?: ValidatorFn[];
  value?: File[];
}

export interface booleanFieldCfg {
  disabled?: boolean;
  validators?: ValidatorFn[];
  value?: boolean;
}

export interface dateFieldCfg {
  disabled?: boolean;
  validators?: ValidatorFn[];
  value?: Date;
}

export interface objectFieldCfg<T> {
  disabled?: boolean;
  validators?: ValidatorFn[];
  value?: T;
}

// String
export const stringFormControl = ({ disabled = false, value = '', validators = [] }: stringFieldCfg = {}) =>
  new FormControl<string>({ value, disabled }, { nonNullable: true, validators: [...validators, Validators.required] });

export const optionalStringFormControl = ({ disabled = false, value = null, validators = [] }: nullableStringFieldCfg = {}) =>
  new FormControl<string | null>({ value, disabled }, { nonNullable: true, validators: validators });

export const emailFormControl = ({ disabled = false, value = '', validators = [Validators.required] }: stringFieldCfg = {}) =>
  new FormControl<string>({ value, disabled }, { nonNullable: true, validators: [...validators, Validators.email] });

export const phoneFormControl = ({ disabled = false, value = '', validators = [Validators.required] }: stringFieldCfg = {}) =>
  new FormControl<string>({ value, disabled }, { nonNullable: true, validators: [...validators, Validators.pattern(phoneNumberRegex)] });

// Number
export const nonZeroFormControl = ({ disabled = false, value = 0, validators = [Validators.required] }: numericFieldCfg = {}) =>
  new FormControl<number>({ value, disabled }, { nonNullable: true, validators: [...validators, nonZeroValidator()] });

export const optionalNonZeroFormControl = ({ disabled = false, value = 0, validators = [Validators.required] }: numericFieldCfg = {}) =>
  new FormControl<number | null>({ value, disabled }, { nonNullable: true, validators: [...validators, nonZeroValidator()] });

export const numericFormControl = ({ disabled = false, value = 0, validators = [] }: numericFieldCfg = {}) =>
  new FormControl<number>({ value, disabled }, { nonNullable: true, validators: [...validators] });

export const optionalNumericFormControl = ({ disabled = false, value = null, validators = [] }: nullableNumericFieldCfg = {}) =>
  new FormControl<number | null>({ value, disabled }, { nonNullable: true, validators: [...validators] });

// Boolean
export const booleanFormControl = ({
  disabled = false,
  value = null as unknown as boolean,
  validators = [Validators.required],
}: booleanFieldCfg = {}) => new FormControl<boolean>({ value, disabled }, { nonNullable: true, validators: [...validators] });

// Date
export const dateFormControl = ({
  disabled = false,
  value = null as unknown as Date,
  validators = [Validators.required],
}: dateFieldCfg = {}) => new FormControl<Date>({ value, disabled }, { nonNullable: true, validators: [...validators] });

export const optionalDateFormControl = ({ disabled = false, value = null as unknown as Date, validators = [] }: dateFieldCfg = {}) =>
  new FormControl<Date | null>({ value, disabled }, { nonNullable: true, validators: [...validators] });

// Object
export const defaultObjectFormControl = <T extends object>({
  disabled = false,
  value = {} as T,
  validators = [Validators.required],
}: objectFieldCfg<T> = {}) => new FormControl<T>({ value, disabled }, { nonNullable: true, validators: [...validators] });

// File
export const fileFormControl = ({ disabled = false, value = [], validators = [Validators.required] }: fileFieldCfg = {}) =>
  new FormControl<File[]>({ value, disabled }, { nonNullable: true, validators: [...validators] });
